import { useEffect, useState } from "react";
import { questionFadeGradient, SCROLL_VAR } from "../lib/timeline/questionFade.js";
import { clientZoom } from "../lib/timeline/questionClearance.js";
import { QUESTION_SELECTOR } from "./useQuestionClearance.js";

/**
 * Mask style for the timeline's forecast layer that dims content under the floating question
 * (see lib/timeline/questionFade.js). Re-measures when the question or the scroll box resizes
 * (wrapping to more lines, a narrower panel, a display-size change) and when `questionKey` (the
 * question text) changes, so the band follows the question.
 *
 * @param {{ enabled: boolean, scrollRef: { current: HTMLElement | null }, questionKey?: unknown }} deps
 * @returns {{ WebkitMaskImage: string, maskImage: string } | null}
 */
export function useQuestionFade({ enabled, scrollRef, questionKey }) {
  const [gradient, setGradient] = useState(null);

  useEffect(() => {
    if (!enabled || typeof ResizeObserver === "undefined") return undefined;
    const scrollEl = scrollRef.current;
    const question = document.querySelector(QUESTION_SELECTOR);
    let raf = null;
    const measure = () => {
      raf = null;
      let next = null;
      if (scrollEl && question) {
        const box = scrollEl.getBoundingClientRect();
        const rect = question.getBoundingClientRect();
        const zoom = clientZoom(box.width, scrollEl.offsetWidth);
        next = questionFadeGradient({
          top: (rect.top - box.top) / zoom,
          bottom: (rect.bottom - box.top) / zoom,
        });
      }
      setGradient((prev) => (prev === next ? prev : next));
    };
    const schedule = () => {
      if (raf == null) raf = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    if (question) observer.observe(question);
    if (scrollEl) observer.observe(scrollEl);
    window.addEventListener("resize", schedule);
    // The masked layer scrolls with the content: publish scrollTop so the gradient's stops
    // stay on the screen (set on the element directly — no re-render per scroll frame).
    const syncScroll = () => scrollEl?.style.setProperty(SCROLL_VAR, `${scrollEl.scrollTop}px`);
    scrollEl?.addEventListener("scroll", syncScroll, { passive: true });
    syncScroll();
    schedule();
    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      scrollEl?.removeEventListener("scroll", syncScroll);
    };
  }, [enabled, scrollRef, questionKey]);

  return enabled && gradient ? { WebkitMaskImage: gradient, maskImage: gradient } : null;
}
